import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Progress } from '../../components/ui/progress';
import { 
  Film, ArrowLeft, Download, ChevronRight, ChevronDown, 
  Lock, Crown, FileText, Circle, CheckCircle, Loader2, X, BookOpen, Trophy, Clock 
} from 'lucide-react';
import { STORY_BEATS } from './constants';

export const MobileLeftSidebar = ({
  showMobileLeftSidebar,
  setShowMobileLeftSidebar,
  expandedActs,
  toggleAct,
  selectedBeat,
  selectBeat,
  canAccessBeat,
  getBeatSceneCount,
  exportScript,
  generateCoverageReport,
  coverageLoading,
  accessLevel,
  challengeEntered,
  enterChallenge,
  enteringChallenge,
  timeLeft
}) => {
  return (
    <AnimatePresence>
      {showMobileLeftSidebar && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMobileLeftSidebar(false)}
            className="md:hidden fixed inset-0 bg-black/80 z-50"
          />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="md:hidden fixed top-0 left-0 bottom-0 w-[85%] max-w-xs bg-secondary z-50 flex flex-col"
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-2" data-testid="mobile-editor-back">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm">Back</span>
                </Link>
                <h3 className="text-white font-bold">Story Beats</h3>
              </div>
              <button onClick={() => setShowMobileLeftSidebar(false)} className="p-2 text-muted-foreground hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="p-2">
                {Object.entries(STORY_BEATS).map(([actNum, actData]) => {
                  const actNumInt = parseInt(actNum);
                  const isExpanded = expandedActs[actNumInt];
                  
                  return (
                    <div key={actNum} className="mb-2">
                      <button
                        onClick={() => toggleAct(actNumInt)}
                        className={`w-full flex items-center justify-between p-3 transition-colors ${
                          isExpanded ? 'bg-primary/20 text-primary' : 'bg-white/5 text-white hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Circle className="w-4 h-4" />
                          <span className="font-bold text-sm">Act {actNum}</span>
                        </div>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                          >
                            <div className="py-1">
                              {actData.beats.map((beat) => {
                                const sceneCount = getBeatSceneCount(beat.id, actNumInt);
                                const isComplete = sceneCount >= beat.suggested_scenes;
                                const isSelected = selectedBeat?.id === beat.id && selectedBeat?.act === actNumInt;
                                const beatLocked = !canAccessBeat(beat.id);
                                
                                return (
                                  <button
                                    key={beat.id}
                                    onClick={() => {
                                      if (!beatLocked) {
                                        selectBeat(beat, actNumInt);
                                        setShowMobileLeftSidebar(false);
                                      }
                                    }}
                                    className={`w-full flex items-center justify-between p-3 pl-6 transition-all ${
                                      beatLocked ? 'opacity-50 cursor-not-allowed' :
                                      isSelected ? 'bg-primary/30 border-l-2 border-primary' : 'hover:bg-white/5 border-l-2 border-transparent'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {beatLocked ? (
                                        <Lock className="w-3 h-3 text-muted-foreground" />
                                      ) : isComplete ? (
                                        <CheckCircle className="w-3 h-3 text-green-400" />
                                      ) : (
                                        <Circle className="w-3 h-3 text-muted-foreground" />
                                      )}
                                      <span className={`text-xs ${beatLocked ? 'text-muted-foreground' : isSelected ? 'text-primary font-medium' : 'text-white'}`}>{beat.name}</span>
                                    </div>
                                    {beatLocked ? (
                                      <span className="text-xs text-primary flex items-center gap-1">
                                        <Crown className="w-3 h-3" />
                                      </span>
                                    ) : (
                                      <span className={`text-xs ${isComplete ? 'text-green-400' : 'text-muted-foreground'}`}>{sceneCount}/{beat.suggested_scenes}</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="p-4 border-t border-white/5 space-y-2">
              <Button onClick={() => { exportScript(); setShowMobileLeftSidebar(false); }} variant="outline" className={`w-full border-white/20 text-white text-xs ${!accessLevel?.access?.can_export ? 'opacity-50' : ''}`}>
                <Download className="w-4 h-4 mr-2" /> Export {!accessLevel?.access?.can_export && <Lock className="w-3 h-3 ml-2" />}
              </Button>
              <Button onClick={() => { generateCoverageReport(); setShowMobileLeftSidebar(false); }} disabled={coverageLoading} className={`w-full text-xs ${accessLevel?.access?.coverage_report ? 'bg-primary text-black' : 'bg-white/10 text-white/50'}`}>
                {coverageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Coverage {!accessLevel?.access?.coverage_report && <Lock className="w-3 h-3 ml-2" />}
              </Button>
            </div>

            {/* Challenge Opt-In (Mobile) */}
            {!challengeEntered && (
              <div className="p-3 border-t border-teal-500/20 bg-gradient-to-r from-teal-500/10 to-transparent">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-teal-400" />
                  <span className="text-xs font-bold text-teal-400">Summer Competition</span>
                </div>
                <p className="text-xs text-teal-200/70 mb-2">Enter your screenplay for a chance to win!</p>
                <Button 
                  onClick={() => { enterChallenge(); setShowMobileLeftSidebar(false); }}
                  disabled={enteringChallenge}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xs py-2"
                >
                  {enteringChallenge ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Entering...</>
                  ) : (
                    <>Enter Challenge</>
                  )}
                </Button>
                <span className="text-[10px] text-teal-300/50 flex items-center justify-center gap-1 mt-1">
                  <Clock className="w-2.5 h-2.5" />
                  {timeLeft?.days || 0}d {timeLeft?.hours || 0}h left
                </span>
              </div>
            )}
            {challengeEntered && (
              <div className="p-3 border-t border-green-500/20 bg-gradient-to-r from-green-500/10 to-transparent">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-xs font-bold text-green-400">Entry Submitted</span>
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export const MobileRightSidebar = ({
  showMobileRightSidebar,
  setShowMobileRightSidebar,
  scenes,
  selectedBeat,
  setSelectedBeat,
  setGeneratedScene,
  setViewingScene,
  setShowFeedbackOptions,
  STORY_BEATS
}) => {
  return (
    <AnimatePresence>
      {showMobileRightSidebar && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMobileRightSidebar(false)}
            className="md:hidden fixed inset-0 bg-black/80 z-50"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="md:hidden fixed top-0 right-0 bottom-0 w-[85%] max-w-xs bg-secondary z-50 flex flex-col"
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Your Scenes
              </h3>
              <button onClick={() => setShowMobileRightSidebar(false)} className="p-2 text-muted-foreground hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-4 text-xs text-muted-foreground">{scenes.length} scene{scenes.length !== 1 ? 's' : ''} created</p>

            <ScrollArea className="flex-1">
              {scenes.length > 0 ? (
                <div className="p-2 space-y-2">
                  {[1, 2, 3].map(actNum => {
                    const actScenes = scenes.filter(s => s.act === actNum);
                    if (actScenes.length === 0) return null;
                    
                    return (
                      <div key={actNum} className="mb-4">
                        <div className="px-2 py-1 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                          Act {actNum}
                        </div>
                        {actScenes.map((scene, idx) => {
                          const beat = Object.values(STORY_BEATS[actNum]?.beats || []).find(b => b.id === scene.beat_id);
                          
                          return (
                            <button
                              key={scene.scene_id}
                              onClick={() => {
                                const beatData = STORY_BEATS[scene.act]?.beats?.find(b => b.id === scene.beat_id);
                                if (beatData) {
                                  setSelectedBeat({ ...beatData, act: scene.act });
                                }
                                setGeneratedScene({
                                  scene_id: scene.scene_id,
                                  heading: scene.heading,
                                  content: scene.action,
                                  saved: true
                                });
                                setViewingScene(true);
                                setShowFeedbackOptions(false);
                                setShowMobileRightSidebar(false);
                              }}
                              className="w-full text-left p-3 rounded-md transition-all bg-white/5 hover:bg-white/10 border border-transparent"
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold bg-white/10 text-muted-foreground">
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium truncate text-white">
                                    {scene.heading || 'Untitled Scene'}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {beat?.name || scene.beat_id}
                                  </p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <Film className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground mb-2">No scenes yet</p>
                  <p className="text-xs text-muted-foreground/70">
                    Make your creative choices to generate scenes
                  </p>
                </div>
              )}
            </ScrollArea>

            {selectedBeat && (
              <div className="p-4 border-t border-white/5 bg-primary/5">
                <p className="text-xs text-muted-foreground mb-1">Currently working on:</p>
                <p className="text-sm text-primary font-medium">{selectedBeat.name}</p>
                <p className="text-xs text-muted-foreground">Act {selectedBeat.act}</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export const MobileHeader = ({
  project,
  setShowMobileLeftSidebar,
  setShowMobileRightSidebar,
  calculateProgress
}) => {
  return (
    <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-secondary/95 backdrop-blur border-b border-white/10">
      <div className="flex items-center justify-between p-3">
        <button
          onClick={() => setShowMobileLeftSidebar(true)}
          className="p-2 bg-white/10 text-white"
          data-testid="mobile-beats-toggle"
        >
          <BookOpen className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-2 flex-1 justify-center">
          <Film className="w-5 h-5 text-primary" />
          <span className="font-bold text-white text-sm truncate max-w-[150px]">{project?.title}</span>
        </div>
        
        <button
          onClick={() => setShowMobileRightSidebar(true)}
          className="p-2 bg-white/10 text-white"
          data-testid="mobile-scenes-toggle"
        >
          <FileText className="w-5 h-5" />
        </button>
      </div>
      
      <div className="px-3 pb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Progress</span>
          <span className="text-xs text-primary font-bold">{calculateProgress()}%</span>
        </div>
        <Progress value={calculateProgress()} className="h-1" />
      </div>
    </div>
  );
};
